//
// Copyright 2023 DXOS.org
//

import varint from 'varint';

import { Event, type Trigger } from '@dxos/async';
import { invariant } from '@dxos/invariant';
import { log } from '@dxos/log';
import { concatUint8Arrays } from '@dxos/util';

import { type DuplexStream } from './duplex-stream.ts';
import { Framer } from './framer.ts';

const MAX_CHUNK_SIZE = 8192;

type Chunk = {
  chunk: Uint8Array;
  channelId: number;
  dataLength?: number;
};

type ChunkEnvelope = {
  msg: Uint8Array;
  trigger?: Trigger;
};

type ChannelBuffer = {
  buffer: Uint8Array;
  msgLength: number;
};

/**
 * Load balancer for handling asynchronous calls from multiple channels.
 *
 * Manages a queue of calls from different channels and ensures that the calls
 * are processed in a balanced manner in a round-robin fashion.
 */
export class Balancer {
  private _lastCallerIndex = 0;
  private _channels: number[] = [];

  private readonly _framer = new Framer();
  // TODO(egorgripasov): Will cause a memory leak if channels do not appreciate the backpressure.
  private readonly _sendBuffers: Map<number, ChunkEnvelope[]> = new Map();
  private readonly _receiveBuffers = new Map<number, ChannelBuffer>();

  private _sending = false;
  public incomingData = new Event<Uint8Array>();
  public readonly stream: DuplexStream = this._framer.stream;
  public readonly closed = this._framer.closed;

  constructor(private readonly _sysChannelId: number) {
    this._channels.push(_sysChannelId);

    // Handle incoming messages.
    this._framer.port.subscribe(this._processIncomingMessage.bind(this));
  }

  get bytesSent() {
    return this._framer.bytesSent;
  }

  get bytesReceived() {
    return this._framer.bytesReceived;
  }

  get buffersCount() {
    return this._sendBuffers.size;
  }

  get readableLength(): number {
    return this._framer.readableLength;
  }

  get writableLength(): number {
    return this._framer.writableLength;
  }

  addChannel(channel: number): void {
    this._channels.push(channel);
  }

  pushData(data: Uint8Array, trigger: Trigger, channelId: number): void {
    this._enqueueChunk(data, trigger, channelId);
    this._sendChunks().catch((err) => log.catch(err));
  }

  destroy(): void {
    if (this._sendBuffers.size !== 0) {
      log.info('destroying balancer with pending calls');
    }
    // Nothing queued will go out now; release the waiters instead of leaving them to time out. They resolve although
    // their frames were dropped: the muxer's destruction, not the send, is how a caller learns the link is gone.
    for (const buffer of this._sendBuffers.values()) {
      for (const { trigger } of buffer) {
        trigger?.wake();
      }
    }
    this._sendBuffers.clear();
    this._framer.destroy();
  }

  private _processIncomingMessage(msg: Uint8Array): void {
    const { channelId, dataLength, chunk } = decodeChunk(msg, (channelId) => !this._receiveBuffers.has(channelId));
    if (!this._receiveBuffers.has(channelId)) {
      if (chunk.length < dataLength!) {
        this._receiveBuffers.set(channelId, {
          // Copied: `chunk` views the framer's receive buffer, which is resliced and reallocated
          // as more bytes arrive, so retaining the view across turns is not safe.
          buffer: new Uint8Array(chunk),
          msgLength: dataLength!,
        });
      } else {
        // Copied for the same reason as below: `chunk` views the framer's receive buffer.
        this.incomingData.emit(new Uint8Array(chunk));
      }
    } else {
      const channelBuffer = this._receiveBuffers.get(channelId)!;
      channelBuffer.buffer = concatUint8Arrays(channelBuffer.buffer, chunk);
      if (channelBuffer.buffer.length < channelBuffer.msgLength) {
        return;
      }
      const msg = channelBuffer.buffer;
      this._receiveBuffers.delete(channelId);
      this.incomingData.emit(msg);
    }
  }

  private _getNextCallerId(): number {
    if (this._sendBuffers.has(this._sysChannelId)) {
      return this._sysChannelId;
    }

    const index = this._lastCallerIndex;
    this._lastCallerIndex = (this._lastCallerIndex + 1) % this._channels.length;

    return this._channels[index];
  }

  private _enqueueChunk(data: Uint8Array, trigger: Trigger, channelId: number): void {
    if (!this._channels.includes(channelId)) {
      throw new Error(`Unknown channel ${channelId}`);
    }

    if (!this._sendBuffers.has(channelId)) {
      this._sendBuffers.set(channelId, []);
    }

    const sendBuffer = this._sendBuffers.get(channelId)!;

    const chunks = [];
    for (let idx = 0; idx < data.length; idx += MAX_CHUNK_SIZE) {
      chunks.push(data.subarray(idx, idx + MAX_CHUNK_SIZE));
    }

    chunks.forEach((chunk, index) => {
      const msg = encodeChunk({
        chunk,
        channelId,
        dataLength: index === 0 ? data.length : undefined,
      });
      sendBuffer.push({ msg, trigger: index === chunks.length - 1 ? trigger : undefined });
    });
  }

  // get the next chunk or null if there are no chunks remaining

  private _getNextChunk(): ChunkEnvelope | null {
    let chunk;
    while (this._sendBuffers.size > 0) {
      const channelId = this._getNextCallerId();
      const sendBuffer = this._sendBuffers.get(channelId);
      if (!sendBuffer) {
        continue;
      }

      chunk = sendBuffer.shift();
      if (!chunk) {
        continue;
      }
      if (sendBuffer.length === 0) {
        this._sendBuffers.delete(channelId);
      }
      return chunk;
    }
    return null;
  }

  private async _sendChunks(): Promise<void> {
    if (this._sending) {
      return;
    }
    this._sending = true;
    let chunk: ChunkEnvelope | null;
    chunk = this._getNextChunk();
    while (chunk) {
      // TODO(nf): determine whether this is needed since we await the chunk send
      if (!this._framer.writable && !this._framer.isClosed) {
        log('PAUSE for drain');
        await this._framer.drain.waitForCount(1);
        log('RESUME for drain');
      }
      try {
        await this._framer.port.send(chunk.msg);
        chunk.trigger?.wake();
      } catch (err: any) {
        log('Error sending chunk', { err });
        chunk.trigger?.throw(err);
      }
      chunk = this._getNextChunk();
    }
    invariant(this._sendBuffers.size === 0, 'sendBuffers not empty');
    this._sending = false;
  }
}

export const encodeChunk = ({ channelId, dataLength, chunk }: Chunk): Uint8Array => {
  const channelTagLength = varint.encodingLength(channelId);
  const dataLengthLength = dataLength ? varint.encodingLength(dataLength) : 0;
  const message = new Uint8Array(channelTagLength + dataLengthLength + chunk.length);
  varint.encode(channelId, message);
  if (dataLength) {
    varint.encode(dataLength, message, channelTagLength);
  }
  message.set(chunk, channelTagLength + dataLengthLength);
  return message;
};

export const decodeChunk = (data: Uint8Array, withLength: (channelId: number) => boolean): Chunk => {
  const channelId = varint.decode(data);
  let dataLength: number | undefined;
  let offset = varint.decode.bytes;

  if (withLength(channelId)) {
    dataLength = varint.decode(data, offset);
    offset += varint.decode.bytes;
  }

  const chunk = data.subarray(offset);

  return { channelId, dataLength, chunk };
};
